import React, { KeyboardEvent, MouseEvent, useMemo } from "react";
import { ButtonWithIcon } from "../buttons/button-with-icon/button-with-icon";
import { IconButton } from "../buttons/icon-button/icon-button";

import documentCheckIcon from "@src/assets/svg/document-check-icon.svg";
import uploadFirmwareArrowIcon from "@src/assets/svg/upload-firmware-arrow.svg";
import deleteGrayIcon from "@src/assets/svg/delete-gray-icon.svg";
import styles from "./drop-area-add-firmware.module.scss";
import { useDropzone } from "react-dropzone";
import {
  acceptStyle,
  rejectStyle,
  stylesDropArea,
} from "./drop-area-add-firmware-dynamic-styles";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { useI18N } from "@src/language/language-context";
import { OtaDrawerDataInterface } from "@src/types/ota-update";

const cssClassName = "drop-area-add-firmware";

const acceptedFirmwareFileTypes = {
  "application/zip": [".zip"],
  "application/x-tar": [
    ".tar",
    ".tar.gz",
    ".tgz",
    ".tar.bz2",
    ".tbz2",
    ".tar.xz",
    ".txz",
  ],
};

interface DropAreaAddFirmwareTypes {
  label: string;
  values: OtaDrawerDataInterface;
  keyProp: string;
  setValues: React.Dispatch<React.SetStateAction<OtaDrawerDataInterface>>;
}

export const DropAreaAddFirmware = ({
  label,
  values,
  keyProp,
  setValues,
}: DropAreaAddFirmwareTypes) => {
  const i18n = useI18N();
  const texts = {
    uploadOrDragAndDropText: i18n(
      "modules.dropAreaAddFirmware.uploadOrDragAndDropText",
    ),
  };
  const { getRootProps, getInputProps, isDragAccept, isDragReject } =
    useDropzone({
      accept: acceptedFirmwareFileTypes,
      onDrop: (acceptedFiles) => {
        setValues((prev: OtaDrawerDataInterface) => {
          return {
            ...prev,
            [keyProp]: acceptedFiles[0],
          };
        });
      },
    });

  const styleDropArea = useMemo(
    () => ({
      ...stylesDropArea,
      ...(isDragAccept ? acceptStyle : {}),
      ...(values[keyProp] ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragAccept, isDragReject, keyProp, values],
  );

  const deleteUploadedFile = (e: MouseEvent | KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setValues({
      ...values,
      [keyProp]: null,
    });
  };

  return (
    <div className={styles[cssClassName]}>
      <label className={`${styles[`${cssClassName}__label`]}`} htmlFor={label}>
        {label}
      </label>
      <div
        {...getRootProps({
          style: styleDropArea,
        })}
      >
        <input {...getInputProps()} id={label} />
        {values[keyProp] !== null ? (
          <FlexRowContainer justifyContent="space-between">
            <ButtonWithIcon
              type="button"
              fun={() => ({})}
              icon={documentCheckIcon}
              variant={["no-outline", "color-grey", "font-thin"]}
              text={values?.["file"]?.["name"]}
            />
            <IconButton fun={deleteUploadedFile} icon={deleteGrayIcon} />
          </FlexRowContainer>
        ) : (
          <>
            <IconButton fun={() => ({})} icon={uploadFirmwareArrowIcon} />
            <p className={`${styles[`${cssClassName}__label`]}`}>
              {texts.uploadOrDragAndDropText}
            </p>
          </>
        )}
      </div>
    </div>
  );
};
